import React, {useEffect, useState} from "react";
import {Button, Grid, styled, Typography} from "@mui/material";
import axios from "axios";
import {retailerCatalogSearch, supplierCatalogSearch} from "../../catalog";
import {ErrorProductTag, NewProductsTag, TotalLiveProductsTag, TotalProductsTag} from "../../../../../../utils/icons";
import DataBox from "./components/DataBox";
import MessageBox from "./components/MessageBox";
import EnhancedCircularLoader from "../../../../../../components/EnhancedCircularLoader";
import {Link} from "react-router-dom";
import ConnectorStatBox from "./components/ConnectorStatBox";
import {loadProfiles} from "../../../../../files/import_export_profiles";
import {formatProfileFromRemote} from "../../../import/product/profile_helpers";
import {SUPPLIER_SYNDICATION_ENABLED} from "../../../../../../utils/utils";
import EnhancedErrorDialog from "../../../../../../components/EnhancedErrorDialog";
import { pageContentGap } from "../../../../../../theme";
import type { ErrorState } from "../../../../types";

// region TypeScript Interfaces
interface DashboardProps {
    isRetailer: boolean;
    availableRetailers: any[];
    retailers?: any[];
    setCurrentCatalog: (catalog: string) => void;
    currentCatalog?: string;
    history?: any;
    location?: any;
}

type StatusCounts = Record<string, number>;
// endregion

let cancelSource = axios.CancelToken.source();

export const StatBox = styled(Grid)(({theme}: any) => ({
    flex: 1,
    // height: "200px",
    minWidth: "500px",
    backgroundColor: "white",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    borderRadius: "6px",
    // margin: "20px",
    padding: "20px",
}));

const StatBoxBtn = styled(Button)(({theme}: any) => ({
    minWidth: "500px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    color: "black",
    textTransform: "none",
    backgroundColor: "white",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    borderRadius: "6px",
    padding: "20px",
    border: "none",
    minHeight: "110px",
}));

const StatBoxTitle = styled(Typography)(({theme}: any) => ({
    fontSize: "14px",
    fontWeight: "600",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    textAlign: "center",
}));

const StatBoxContent = styled(Grid)(({theme}: any) => ({
    fontSize: "26px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

const Tag = styled(Grid)(({theme}: any) => ({
    // width: "40px",
    // height: "40px",
    borderRadius: "50%",
    margin: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

const Text = styled(Grid)(({theme}: any) => ({
    // width: "30px",
    // height: "30px",
    borderRadius: "50%",
    // margin: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

export const keptSyncStatuses = ["All", "Listing Pending", "Not Listed", "Listing Failed", "Delisted", "Listed", "Error"];
export const keptApprovalStatues = ["All", "New", "Approved", "Not Compliant", "Update Suggested", "Error"];
const partnerColorMap = ["#01B8AA", "#F2C80F", "#F9A456", "#FD625E", "#5F6B6D", "#374649"];
const categoryColorMap = ["#01B8AA", "#F2C80F", "#F9A456", "#FD625E", "#5F6B6D", "#374649"];
export const supplierColorMap = {
    "New": "#0099FF",
    "Approved": "#01B8AA",
    "Not Compliant": "#F9A456",
    "Update Suggested": "#F2C80F",
    "Error": "#FD625E",
};
export const retailerColorMap = {
    "Listing Pending": "#F2C80F",
    "Not Listed": "#5F6B6D",
    "Listing Failed": "#F9A456",
    "Delisted": "#374649",
    "Listed": "#01B8AA",
    "Error": "#FD625E"
};

export default function Dashboard(props: DashboardProps) {
    const isRetailer = props.isRetailer;
    const availableRetailers = props.availableRetailers;

    const isMarketplace = props.retailers && props.retailers.some((retailer: any) => retailer.Marketplace === true);
    const isDropship = props.retailers && props.retailers.some((retailer: any) => retailer.Marketplace === false);

    const {setCurrentCatalog} = props;

    const [myProductListingStatuses, setMyProductListingStatuses] = useState<StatusCounts>({});
    const [myProductApprovalStatuses, setMyProductApprovalStatuses] = useState<StatusCounts>({});

    const [myProductCategories, setMyProductCategories] = useState<StatusCounts>({});
    const [productsByPartner, setProductsByPartner] = useState<StatusCounts>({});
    const [partnerProducts, setPartnerProducts] = useState<StatusCounts>({});

    const [error, setError] = useState<ErrorState>({open: false, errorHeader: "", errorMessage: ""});
    const [loading, setLoading] = useState(true);
    const [profiles, setProfiles] = useState<any[]>([]);

    useEffect(() => {
        let url = isRetailer ? retailerCatalogSearch : supplierCatalogSearch;

        const getMyProductListingStatuses = async () => {
            // General Product Status //
            let counts: StatusCounts = {};
            let searchStatus = "syncStatus";

            const productResponse = await axios.get(url + `?$top=0&$count=true&facets=['${searchStatus}', 'merchantId']`, {
                cancelToken: cancelSource.token
            });

            counts["All"] = productResponse.data["@odata.count"];
            productResponse.data["facets"][searchStatus].forEach((value: any) => {
                counts[value.value] = value.count;
            });

            // Product Errors //
            let errorCount = 0;
            const errorResponse = await axios.get(url + `?$top=0&$count=true&facets=['errorMessage']`, {
                cancelToken: cancelSource.token
            });
            errorResponse.data["facets"]["errorMessage"].forEach((value: any) => {
                errorCount += value.count;
            });
            counts["Error"] = errorCount;

            // Filter Out Unnecessary Statuses //
            let formattedCounts: StatusCounts = {};
            Object.keys(counts).forEach((key) => {
                if (keptSyncStatuses.includes(key)) {
                    formattedCounts[key] = counts[key];
                }
            });

            // Update State //
            setMyProductListingStatuses(formattedCounts);
        }

        const getMyProductApprovalStatuses = async () => {
            // General Product Status //
            let counts: StatusCounts = {};
            let searchStatus = "approvalStatus";

            const productResponse = await axios.get(url + `?$top=0&$count=true&facets=['${searchStatus}']`, {
                cancelToken: cancelSource.token
            });

            counts["All"] = productResponse.data["@odata.count"];
            productResponse.data["facets"][searchStatus].forEach((value: any) => {
                counts[value.value] = value.count;
            });

            // Product Errors //
            let errorCount = 0;
            const errorResponse = await axios.get(url + `?$top=0&$count=true&facets=['errorMessage']`, {
                cancelToken: cancelSource.token
            });
            errorResponse.data["facets"]["errorMessage"].forEach((value: any) => {
                errorCount += value.count;
            });
            counts["Error"] = errorCount;

            // Filter Out Unnecessary Statuses //
            let formattedCounts: StatusCounts = {};
            Object.keys(counts).forEach((key) => {
                if (keptApprovalStatues.includes(key)) {
                    formattedCounts[key] = counts[key];
                }
            });

            // Update State //
            setMyProductApprovalStatuses(formattedCounts);
        }

        const getMyProductCategories = async () => {
            let counts: StatusCounts = {};
            let url = "";
            let searchStatus = "category";

            if (isRetailer) {
                url = retailerCatalogSearch;
            } else {
                url = supplierCatalogSearch;
            }

            axios.get(url + `?$top=0&$count=true&facets=['${searchStatus}']`, {
                cancelToken: cancelSource.token
            }).then((response) => {
                response.data["facets"][searchStatus].forEach((value: any) => {
                    counts[value.value] = value.count;
                });

                if (Object.keys(counts).length > 10) {
                    let temp: StatusCounts = {};
                    let keys = Object.keys(counts);
                    for (let i = 0; i < 5; i++)
                        temp[keys[i]] = counts[keys[i]];
                    counts = temp;
                }

                // Limit object length to
                const temp: StatusCounts = {};
                const keys = Object.keys(counts);
                for (let i = 0; i < 5; i++) {
                    if (keys[i] !== undefined)
                        temp[keys[i]] = counts[keys[i]];
                }

                temp["All"] = Object.keys(counts).length;

                setMyProductCategories(temp);
            });
        }

        const getPartnerProductStatuses = async () => {
            // General Product Status //
            let counts: StatusCounts = {};
            let url = supplierCatalogSearch;
            let searchStatus = "approvalStatus";

            const productResponse = await axios.get(url + `?$top=0&$count=true&facets=['${searchStatus}']`, {
                cancelToken: cancelSource.token
            });

            counts["All"] = productResponse.data["@odata.count"];
            productResponse.data["facets"][searchStatus].forEach((value: any) => {
                counts[value.value] = value.count;
            });

            // Product Errors //
            let errorCount = 0;
            const errorResponse = await axios.get(url + `?$top=0&$count=true&facets=['errorMessage']`, {
                cancelToken: cancelSource.token
            });
            errorResponse.data["facets"]["errorMessage"].forEach((value: any) => {
                errorCount += value.count;
            });
            counts["Error"] = errorCount;

            // Filter Out Unnecessary Statuses //
            const formattedCounts: StatusCounts = {};
            Object.keys(counts).forEach((key) => {
                if (keptApprovalStatues.includes(key)) {
                    formattedCounts[key] = counts[key];
                }
            });

            // Update State //
            setPartnerProducts(formattedCounts);
        }

        const getProductsByPartner = async () => {
            let counts: StatusCounts = {};
            let url = supplierCatalogSearch;
            let searchStatus = "supplierName";

            axios.get(url + `?$top=0&$count=true&facets=['${searchStatus}']`, {
                cancelToken: cancelSource.token
            }).then((response) => {
                counts["All"] = response.data["@odata.count"];
                response.data["facets"][searchStatus].forEach((value: any) => {
                    counts[value.value] = value.count;
                });
            });

            // Update State //
            setProductsByPartner(counts);
        }

        const loadImportProfiles = async () => {
            let profiles = await loadProfiles("ImportCatalogItems");
            setProfiles(profiles.map((profile: any) => formatProfileFromRemote(profile)));
        }

        const getAll = async () => {
            setLoading(true);
            try {
                const listingStatusesResponse = getMyProductListingStatuses();
                const approvalStatusesResponse = getMyProductApprovalStatuses();
                const partnerProductStatusesResponse = getPartnerProductStatuses();
                const myProductCategoriesResponse = getMyProductCategories();
                const getProductsByPartnerResponse = getProductsByPartner();
                const loadImportProfilesResponse = loadImportProfiles();
                await Promise.all(
                    [listingStatusesResponse,
                        approvalStatusesResponse,
                        partnerProductStatusesResponse,
                        myProductCategoriesResponse,
                        getProductsByPartnerResponse,
                        loadImportProfilesResponse]
                );
            } catch (error: any) {
                setError({
                    open: true,
                    errorHeader: "Error getting product data, please refresh.",
                    errorMessage: error?.message ?? String(error)
                })
            }
            setLoading(false);
        }

        getAll();

        return () => {
            cancelSource.cancel("Component unmounted");
            cancelSource = axios.CancelToken.source();
        }

        // eslint-disable-next-line
    }, [props.currentCatalog]);

    return (
        <Grid container style={{gap: pageContentGap, width: "100%"}}>
            <Grid container style={{flexWrap: "wrap", gap: pageContentGap, width: "100%"}}>
                {/*Total Products Available*/}
                <Link to={"/productonboardingcenter/catalog?catalog=partnercatalog"}
                      style={{flex: 1}}
                >
                    <StatBoxBtn style={{minWidth: "228px", height: "auto", width: "100%"}}
                                onClick={() => setCurrentCatalog("partnercatalog")}
                    >
                        <StatBoxTitle>
                            Total Products Available
                        </StatBoxTitle>
                        <StatBoxContent>
                            <Tag><TotalProductsTag style={{width: "20px", height: "20px"}}/></Tag>
                            <Text>{partnerProducts["All"] ?? "0"}</Text>
                        </StatBoxContent>
                    </StatBoxBtn>
                </Link>

                {(isMarketplace && SUPPLIER_SYNDICATION_ENABLED) && (
                    <Link
                        to={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=syncStatus%20eq%20Listing%20Pending"}
                        style={{flex: 1}}
                    >
                        <StatBoxBtn style={{minWidth: "228px", height: "auto", width: "100%"}}
                                    onClick={() => setCurrentCatalog("mycatalog")}
                        >
                            <StatBoxTitle>
                                Total Products Pending
                            </StatBoxTitle>
                            <StatBoxContent>
                                <Tag><TotalProductsTag style={{width: "20px", height: "20px"}}/></Tag>
                                <Text>{myProductListingStatuses["Listing Pending"] ?? "0"}</Text>
                            </StatBoxContent>
                        </StatBoxBtn>
                    </Link>
                )}

                {/*New Products*/}
                {(isRetailer || isDropship) && (
                    <Link
                        to={`/productonboardingcenter/catalog?q.0=approvalStatus%20eq%20New&catalog=${isRetailer ? "partnercatalog" : "mycatalog"}`}
                        style={{flex: 1}}
                    >
                        <StatBoxBtn style={{minWidth: "228px", height: "auto", width: "100%"}}
                                    onClick={() => setCurrentCatalog(isRetailer ? "partnercatalog" : "mycatalog")}
                        >
                            <StatBoxTitle>
                                Total New Products
                            </StatBoxTitle>
                            <StatBoxContent>
                                <Tag><NewProductsTag style={{width: "20px", height: "20px"}}/></Tag>
                                <Text>
                                    {isRetailer ? partnerProducts["New"] ?? "0" : myProductApprovalStatuses["New"] ?? "0"}
                                </Text>
                            </StatBoxContent>
                        </StatBoxBtn>
                    </Link>
                )}

                {/*Total Listed Products*/}
                {(isRetailer || (isMarketplace && SUPPLIER_SYNDICATION_ENABLED)) && (
                    <Link
                        to={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=syncStatus%20eq%20Listed"}
                        style={{flex: 1}}
                    >
                        <StatBoxBtn style={{minWidth: "228px", height: "auto", width: "100%"}}
                                    onClick={() => setCurrentCatalog("mycatalog")}
                        >
                            <StatBoxTitle>
                                {isRetailer ? "Total Listed Products" : "Total Products Synced"}
                            </StatBoxTitle>
                            <StatBoxContent>
                                <Tag><TotalLiveProductsTag style={{width: "20px", height: "20px"}}/></Tag>
                                <Text>
                                    {myProductListingStatuses["Listed"] ?? "0"}
                                </Text>
                            </StatBoxContent>
                        </StatBoxBtn>
                    </Link>
                )}

                {/*Total Approved Products*/}
                {(isRetailer || isDropship) && (
                    <Link
                        to={`/productonboardingcenter/catalog?catalog=${isRetailer ? "partnercatalog" : "mycatalog"}&q.0=approvalStatus%20eq%20Approved`}
                        style={{flex: 1}}
                    >
                        <StatBoxBtn style={{minWidth: "228px", height: "auto", width: "100%"}}
                                    onClick={() => setCurrentCatalog(isRetailer ? "partnercatalog" : "mycatalog")}
                        >
                            <StatBoxTitle>
                                Total Approved Products
                            </StatBoxTitle>
                            <StatBoxContent>
                                <Tag><TotalLiveProductsTag style={{width: "20px", height: "20px"}}/></Tag>
                                <Text>
                                    {myProductApprovalStatuses["Approved"] ?? "0"}
                                </Text>
                            </StatBoxContent>
                        </StatBoxBtn>
                    </Link>
                )}

                {/*Total Product Errors*/}
                {(isRetailer || isDropship) && (
                    <Link to={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=errorMessage%20ne%20null"}
                          style={{flex: 1}}
                    >
                        <StatBoxBtn style={{minWidth: "228px", height: "auto", width: "100%"}}
                                    onClick={() => setCurrentCatalog("mycatalog")}
                        >
                            <StatBoxTitle>
                                Total Products With Errors
                            </StatBoxTitle>
                            <StatBoxContent>
                                <Tag><ErrorProductTag style={{width: "20px", height: "20px"}}/></Tag>
                                <Text>{myProductApprovalStatuses["Error"] ?? "0"}</Text>
                            </StatBoxContent>
                        </StatBoxBtn>
                    </Link>
                )}

                {/*Total Listing Errors*/}
                {(isRetailer || (isMarketplace && SUPPLIER_SYNDICATION_ENABLED)) && (
                    <Link
                        to={"/productonboardingcenter/catalog?catalog=mycatalog&q.0=syncStatus%20eq%20Listing%20Failed"}
                        style={{flex: 1}}
                    >
                        <StatBoxBtn style={{minWidth: "228px", height: "auto", width: "100%"}}
                                    onClick={() => setCurrentCatalog("mycatalog")}
                        >
                            <StatBoxTitle>
                                Total Products Listing Failed
                            </StatBoxTitle>
                            <StatBoxContent>
                                <Tag><ErrorProductTag style={{width: "20px", height: "20px"}}/></Tag>
                                <Text>{myProductListingStatuses["Listing Failed"] ?? "0"}</Text>
                            </StatBoxContent>
                        </StatBoxBtn>
                    </Link>
                )}
            </Grid>

            {/*Stat Graphs*/}
            <Grid container style={{flexWrap: "wrap", gap: pageContentGap, width: "100%"}}>
                {/*Connector Graphs*/}
                {(!props.isRetailer && props.retailers && isMarketplace && SUPPLIER_SYNDICATION_ENABLED) && (
                    <>
                        {loading ? (
                            <StatBox container style={{flex: 1, minWidth: "48%"}}>
                                <EnhancedCircularLoader/>
                            </StatBox>
                        ) : (
                            <>
                                {props.retailers!.map((retailer: any) => {
                                    if (retailer.Marketplace) {
                                        return (
                                            <ConnectorStatBox
                                                profiles={profiles}
                                                setError={setError}
                                                key={retailer.Id}
                                                connectorId={retailer.Id}
                                                availableRetailers={availableRetailers}
                                            />
                                        );
                                    }
                                    else {
                                        return <></>
                                    }
                                })}
                            </>
                        )}
                    </>
                )}

                {isRetailer && (
                    <StatBox container style={{flex: 1, minWidth: "50%"}}>
                        {loading ? (
                            <EnhancedCircularLoader/>
                        ) : (
                            <DataBox title={"Partner Catalogs"}
                                     datums={partnerProducts}
                                     colorMap={supplierColorMap}
                                     totalText={"Total # of Products"}
                                     type={"pie"}
                                     showLegend={true}
                            />
                        )}
                    </StatBox>
                )}

                {(isRetailer || isDropship) && (
                    <StatBox container style={{flex: 1, minWidth: "48%"}}>
                        {loading ? (
                            <EnhancedCircularLoader/>
                        ) : (
                            <DataBox title={"My Catalog"}
                                     datums={isRetailer ? myProductListingStatuses : myProductApprovalStatuses}
                                     colorMap={isRetailer ? retailerColorMap : supplierColorMap}
                                     totalText={"Total # of Products"}
                                     type={"pie"}
                                     showLegend={true}
                            />
                        )}
                    </StatBox>
                )}

                <StatBox container style={{flex: 1, minWidth: "48%"}}>
                    {loading ? (
                        <EnhancedCircularLoader/>
                    ) : (
                        <DataBox title={"Top 5 Categories"}
                                 datums={myProductCategories}
                                 colorMap={categoryColorMap}
                                 totalText={"Total # of Categories"}
                                 type={"pie"}
                                 showLegend={true}
                        />
                    )}
                </StatBox>
            </Grid>

            <Grid container style={{gap: pageContentGap, width: "100%"}}>
                <StatBox container style={{height: "500px"}}>
                    <MessageBox/>
                </StatBox>

                {isRetailer && (
                    <StatBox container style={{height: "500px"}}>
                        {loading ? (
                            <EnhancedCircularLoader/>
                        ) : (
                            <DataBox title={"Numbers of Products By Partner"}
                                     datums={productsByPartner}
                                     colorMap={partnerColorMap}
                                     totalText={"Total # of Products"}
                                     type={"pie"}
                                     showLegend={true}
                            />
                        )}
                    </StatBox>
                )}
            </Grid>

            <EnhancedErrorDialog open={error.open}
                         errorHeader={error.errorHeader}
                         errorMessage={error.errorMessage}
                         onClose={() => setError({...error, open: false})}
            />
        </Grid>
    )
}
